<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useClipboard, useEventBus } from "@vueuse/core"
import { computed, inject, ref } from "vue"
import { useRouter } from "vue-router"
import { useToast } from "primevue/usetoast"
import { makeDraggable } from "@vue-dnd-kit/core"
import { useRedirect, type RedirectParams } from "@core/composables"
import { useResponsive } from "@core/composables/useResponsive"
import { useAuthStore } from "@core/stores/auth.store"
import { WEATHER_STATION_TYPES_LIST } from "@core/constants"
import type { Building, Source } from "@types"
import type { HomeBuildingNode } from "@modules/home/stores/home-list.types"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import {
    DASHBOARD_WORKSPACE_DND_GROUP,
    type WorkspaceDragPayload,
} from "@modules/home/composables/homeDnDHandler"
import { HOME_BUILDING_CARD_MIN_HEIGHT } from "@modules/home/components/dashboard/building/homeBuildingCardLayout"
import type { Services } from "@services"
import { ServiceError } from "@core/exceptions"

defineOptions({
    name: "HomeBuildingRow",
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    building: HomeBuildingNode
    depth?: number
    folderList?: boolean
    folderId?: string
    draggable?: boolean
    showDelete?: boolean
    disableTags?: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    depth: 0,
    folderList: false,
    folderId: undefined,
    draggable: true,
    showDelete: true,
    disableTags: false,
})

// ================================
// COMPOSABLES
// ================================
const router = useRouter()
const toast = useToast()
const { redirectWrapper } = useRedirect()
const { isTablet } = useResponsive()
const homeWorkspace = useHomeWorkspaceStore()
const authStore = useAuthStore()
const { $deleteBuildingTag } = inject<Services>("services")!
const { copy } = useClipboard()
const notificationBus = useEventBus("notification")

// ================================
// STATE
// ================================
type ContextMenuExpose = {
    show: (event: MouseEvent) => void
}

const contextMenu = ref<ContextMenuExpose | null>(null)
const contextMenuModel = ref<
    { label: string; icon?: string; command?: () => void }[]
>([])
/**
 * Si défini, le menu contextuel est limité aux actions source (clic droit sur une ligne source).
 */
const contextMenuExtraSourceId = ref<string | null>(null)
const cardRef = ref<HTMLElement | null>(null)
const suppressBuildingNavClick = ref(false)
const getDragDisabled = computed(() => !props.draggable || isTablet.value)
const isExpanded = ref(true)
/** Panneau liste des sources (accordéon). */
const isSourcesExpanded = ref(false)
/** Étiquette en cours de retrait (désactive le chip correspondant). */
const tagRemoving = ref<string | null>(null)

/** Nombre maximal d’étiquettes en chips détaillés avant le regroupement « +X ». */
const TAGS_ROW_VISIBLE_MAX = 3

// ================================
// COMPUTED
// ================================
/** Identifiant métier du bâtiment. */
const getBuildingId = computed(() => {
    const b = props.building as Building
    return b._id_building ?? b._id
})

/**
 * Suppression réservée aux admin/owner propriétaires du bâtiment (table des
 * droits DEV-16282 : Membre et Utilisateur ne peuvent pas supprimer). Évalué
 * par bâtiment via ses propres `rights` (l'arbre mélange owns / partagés).
 */
const getCanDeleteBuilding = computed(() => {
    const rights = (props.building as Building).rights
    return (
        authStore.isCompanyAdmin &&
        Array.isArray(rights) &&
        rights.includes("owns")
    )
})

/** Données affichées par la ligne récap bâtiment. */
const getItemBuildingData = computed(() => {
    const b = props.building as Building
    const tags = (b.tags ?? []).map((t) => (typeof t === "string" ? t : t.name))
    return {
        _id: getBuildingId.value,
        name: b.name,
        niveau: b.infos?.niveau,
        address: b.address,
        tags,
        visible_to: b.visible_to,
        rights: b.rights,
    }
})

/** Libellés d’étiquettes pour la ligne (hors `ItemBuilding`, affichage local). */
const getBuildingTagLabels = computed(
    () => getItemBuildingData.value.tags ?? []
)

/** Nombre d’étiquettes affiché dans « Etiquettes (X) ». */
const getBuildingTagCount = computed(() => getBuildingTagLabels.value.length)

/** Étiquettes affichées en chips (max 3). */
const getBuildingTagsDisplayed = computed(() =>
    getBuildingTagLabels.value.slice(0, TAGS_ROW_VISIBLE_MAX)
)

/** Étiquettes regroupées derrière le chip « +X ». */
const getBuildingTagsOverflow = computed(() =>
    getBuildingTagLabels.value.slice(TAGS_ROW_VISIBLE_MAX)
)

/** Nombre d’étiquettes dans le surplus (pour le libellé +X). */
const getBuildingTagOverflowCount = computed(
    () => getBuildingTagsOverflow.value.length
)

/** Texte du tooltip listant les étiquettes supplémentaires. */
const getBuildingTagsOverflowTooltipText = computed(() =>
    getBuildingTagsOverflow.value.map((t) => `• ${t}`).join("\n")
)

/** Sources rattachées au bâtiment. */
const getSources = computed(() => {
    const b = props.building as Building
    return (b.sources ?? []) as Source[]
})

/** Au moins une source à afficher (bouton + panneau). */
const getHasSources = computed(() => getSources.value.length > 0)

/** Enfants directs du bâtiment courant. */
const getChildren = computed(() => {
    return (props.building.children ?? []) as HomeBuildingNode[]
})

/** Indique si la ligne possède des sous-bâtiments. */
const getHasChildren = computed(() => getChildren.value.length > 0)

/** Tous les ids du sous-arbre pour les actions de déplacement. */
const getTreeIds = computed(() => collectTreeIds(props.building))

/**
 * URL absolue de la fiche données du bâtiment (alignée sur « Ouvrir »).
 */
function getBuildingDataAbsoluteUrl(buildingId: string): string {
    const { fullPath } = router.resolve({
        path: `/building/${buildingId}/data`,
    })
    return `${window.location.origin}${fullPath}`
}

/**
 * URL absolue de la fiche données de la source (alignée sur la navigation source).
 */
function getSourceDataAbsoluteUrl(sourceId: string): string {
    const { fullPath } = router.resolve({
        path: `/source/${sourceId}/data`,
    })
    return `${window.location.origin}${fullPath}`
}

/** Entrées du menu contextuel : menu bâtiment complet, ou menu source réduit si clic droit sur une source. */
const getMenuItems = computed(() => {
    const extraSourceId = contextMenuExtraSourceId.value
    if (extraSourceId) {
        return [
            {
                label: "Ouvrir",
                icon: "pi pi-external-link",
                command: () =>
                    redirectToSourceRoute("/source/:_id_source/data", {
                        _id_source: extraSourceId,
                    }),
            },
            {
                label: "Copier le lien de la source",
                icon: "pi pi-link",
                command: () => {
                    copy(getSourceDataAbsoluteUrl(extraSourceId))
                    notificationBus.emit({
                        severity: "success",
                        title: "Lien copié dans le presse-papier",
                        life: 3000,
                    })
                },
            },
        ]
    }

    const buildingId = getBuildingId.value
    return [
        {
            label: "Ouvrir",
            icon: "pi pi-external-link",
            command: () =>
                pushBuildingRoute({
                    path: `/building/${buildingId}/data`,
                }),
        },
        {
            label: "Renommer",
            icon: "pi pi-pencil",
            command: () =>
                homeWorkspace.openModal("buildingRename", {
                    buildingId: getBuildingId.value,
                    name: (props.building as Building).name,
                    folderList: props.folderList,
                }),
        },
        {
            label: "Déplacer",
            icon: "pi pi-arrows-alt",
            command: () =>
                homeWorkspace.openModal("buildingMove", {
                    buildingIds: getTreeIds.value,
                    folderId: props.folderId ?? "",
                    currentFolderId: props.folderId ?? "",
                }),
        },
        ...(props.showDelete && getCanDeleteBuilding.value
            ? [
                  {
                      label: "Supprimer",
                      icon: "pi pi-trash",
                      command: () =>
                          homeWorkspace.openModal("buildingRemove", {
                              buildingId: getBuildingId.value,
                          }),
                  },
              ]
            : []),
        {
            label: "Partage",
            icon: "pi pi-users",
            command: () =>
                homeWorkspace.openModal("buildingAccess", {
                    buildingId: getBuildingId.value,
                    itemBuilding: getItemBuildingData.value,
                }),
        },
        {
            label: "Copier le lien vers le bâtiment",
            icon: "pi pi-home",
            command: () => {
                copy(getBuildingDataAbsoluteUrl(buildingId))
                notificationBus.emit({
                    severity: "success",
                    title: "Lien copié dans le presse-papier",
                    life: 3000,
                })
            },
        },
    ]
})

/** Réinitialise le garde-fou si aucun clic n’a été consommé (drop sans événement sur la carte). */
const SUPPRESS_NAV_CLICK_FALLBACK_MS = 250

/**
 * Marque une zone de la carte qui ne doit pas ouvrir le bâtiment (boutons, panneau sources).
 * Utilisé avec `closest()` dans le gestionnaire de clic sur la carte.
 */
const BUILDING_CARD_NO_NAV_ATTR = "data-dashboard-building-no-nav"

function armSuppressBuildingNavAfterDrag(): void {
    suppressBuildingNavClick.value = true
    window.setTimeout(() => {
        suppressBuildingNavClick.value = false
    }, SUPPRESS_NAV_CLICK_FALLBACK_MS)
}

const { isDragging } = makeDraggable(cardRef, {
    groups: [DASHBOARD_WORKSPACE_DND_GROUP],
    /** Aligné sur `HomeFolderRow` : seuil de mouvement avant drag, clic = navigation. */
    activation: { distance: 8 },
    disabled: getDragDisabled,
    data: (): WorkspaceDragPayload => ({
        type: "building",
        buildingIds: [...getTreeIds.value],
    }),
    events: {
        onSelfDragEnd: armSuppressBuildingNavAfterDrag,
        onSelfDragCancel: armSuppressBuildingNavAfterDrag,
    },
})

// ================================
// INTERACTIONS
// ================================
/**
 * Navigation vers une route bâtiment : vide le cache dossiers / listes pour un rechargement propre au retour sur l’accueil dossiers.
 */
function pushBuildingRoute(options: {
    path: string
    query?: Record<string, string | undefined>
}): void {
    homeWorkspace.clearWorkspaceListsOnBuildingNavigation()
    void router.push(options)
}

/**
 * Navigation vers une route source (`redirectWrapper`) : même nettoyage store que pour un bâtiment.
 */
function redirectToSourceRoute(url: string, params?: RedirectParams): void {
    homeWorkspace.clearWorkspaceListsOnBuildingNavigation()
    redirectWrapper(url, params)
}

/**
 * Intercepte le clic synthétique après un drag en phase capture (avant le bouton titre).
 */
function onBuildingCardClickCapture(ev: MouseEvent): void {
    if (!suppressBuildingNavClick.value) return
    ev.preventDefault()
    ev.stopPropagation()
    suppressBuildingNavClick.value = false
}

/**
 * Clic sur la carte bâtiment : navigation sauf si la cible est dans une zone exclue ou après drag.
 */
function onBuildingCardNavigate(ev: MouseEvent): void {
    if (suppressBuildingNavClick.value) {
        return
    }
    const t = ev.target
    if (t instanceof Element && t.closest(`[${BUILDING_CARD_NO_NAV_ATTR}]`)) {
        return
    }
    pushBuildingRoute({
        path: `/building/${getBuildingId.value}/data`,
    })
}

/**
 * Affiche le menu d’actions (bouton ⋮ ou clic droit sur la carte).
 */
function onToggleMenu(ev: Event): void {
    contextMenuExtraSourceId.value = null
    contextMenuModel.value = getMenuItems.value
    contextMenu.value?.show(ev as MouseEvent)
}

/**
 * Remplace le menu navigateur par le menu actions bâtiment.
 */
function onBuildingRowContextMenu(ev: MouseEvent): void {
    ev.preventDefault()
    contextMenuExtraSourceId.value = null
    contextMenuModel.value = getMenuItems.value
    contextMenu.value?.show(ev)
}

/**
 * Clic droit sur une ligne source : même menu que la carte + entrée « Copier le lien vers la source ».
 */
function onSourceRowContextMenu(ev: MouseEvent, src: Source): void {
    ev.preventDefault()
    ev.stopPropagation()
    const id = src._id_source ?? (src as { _id?: string })._id ?? ""
    contextMenuExtraSourceId.value = id || null
    contextMenuModel.value = getMenuItems.value
    contextMenu.value?.show(ev)
}

/**
 * Ouvre ou ferme l'affichage du sous-arbre de bâtiments.
 */
function onToggleExpand(): void {
    isExpanded.value = !isExpanded.value
}

/**
 * Ouvre ou ferme la liste des sources (accordéon).
 */
function onToggleSources(): void {
    isSourcesExpanded.value = !isSourcesExpanded.value
}

/**
 * Ouvre le dialogue d’ajout d’étiquettes (multi-sélection + nouvelles étiquettes).
 */
function onOpenBuildingTagsDialog(ev: MouseEvent): void {
    ev.preventDefault()
    ev.stopPropagation()
    homeWorkspace.openModal("buildingTags", {
        buildingId: getBuildingId.value,
        tagNames: [...getBuildingTagLabels.value],
        name: (props.building as Building).name,
    })
}

/**
 * Retire une étiquette du bâtiment (API + mise à jour locale des listes).
 */
async function onRemoveBuildingTag(tag: string): Promise<void> {
    const buildingId = getBuildingId.value
    if (!buildingId || tagRemoving.value) return
    tagRemoving.value = tag
    try {
        await $deleteBuildingTag(buildingId, [tag])
        const next = getBuildingTagLabels.value.filter((t) => t !== tag)
        homeWorkspace.applyBuildingTagsUpdate(buildingId, next)
    } catch (err) {
        const msg =
            err instanceof ServiceError
                ? err.message
                : err instanceof Error
                  ? err.message
                  : "Suppression impossible"
        toast.add({
            group: "notifications",
            severity: "error",
            summary: "Étiquettes",
            detail: msg,
            life: 6000,
        })
    } finally {
        tagRemoving.value = null
    }
}

/**
 * Ouvre la page données de la source (bouton graphique ou clic sur la ligne).
 */
function onSourceClickData(payload: { id: string }): void {
    if (!payload.id) return
    redirectToSourceRoute("/source/:_id_source/data", {
        _id_source: payload.id,
    })
}

/**
 * Ouvre les infos générales de la source, ou la vue station météo sur le bâtiment.
 */
function onSourceClickSettings(payload: { id: string; type: string }): void {
    if (!payload.id) return
    const type = payload.type ?? ""
    if (WEATHER_STATION_TYPES_LIST.includes(String(type))) {
        pushBuildingRoute({
            path: `/building/${getBuildingId.value}/station-meteo`,
            query: {
                _id_source: payload.id,
            },
        })
    } else {
        redirectToSourceRoute("/source/:_id_source/info-general", {
            _id_source: payload.id,
        })
    }
}

/**
 * Clic / clavier sur la carte source : navigation vers les données (hors boutons et zone actions).
 */
function onSourceCardActivate(
    ev: MouseEvent | KeyboardEvent,
    src: Source
): void {
    if (ev instanceof MouseEvent && ev.button !== 0) return
    const target = ev.target
    if (!(target instanceof Element)) return
    if (target.closest(".item-source__actions") || target.closest("button")) {
        return
    }
    const id = src._id_source ?? (src as { _id?: string })._id ?? ""
    if (id) onSourceClickData({ id })
}

/**
 * Récupère la liste d'ids d'un bâtiment et de tous ses descendants.
 * @param node - Noeud racine du sous-arbre
 * @returns Identifiants du sous-arbre
 */
function collectTreeIds(node: HomeBuildingNode): string[] {
    const nodeId = (node._id_building ?? node._id) as string
    const children = (node.children ?? []) as HomeBuildingNode[]
    return [
        nodeId,
        ...children.flatMap((child) => collectTreeIds(child)),
    ].filter(Boolean)
}

/** Indique si la ligne est un enfant dans l’arborescence (traits de liaison). */
const getIsNested = computed(() => (props.depth ?? 0) > 0)

/**
 * Z-index par profondeur : les ancêtres restent au-dessus des traits qui
 * descendent vers les enfants (cf. legacy BuildingListItem).
 */
const getRowZIndex = computed(() => 30 - (props.depth ?? 0) * 10)

/** Hauteur minimale commune des cartes bâtiment (liste dashboard). */
const getCardMinHeightStyle = computed(() => ({
    minHeight: HOME_BUILDING_CARD_MIN_HEIGHT,
}))

/** Nombre maximum de sources visibles avant défilement vertical du panneau. */
const SOURCES_PANEL_MAX_VISIBLE_SOURCES = 5

/** Hauteur estimée d’une carte ItemSource dans ce panneau (hors gap entre lignes). */
const SOURCES_PANEL_ESTIMATED_SOURCE_ROW_HEIGHT_REM = "6.75rem"

/** Équivalent Tailwind `gap-2` entre les ItemSource (0.5rem). */
const SOURCES_PANEL_SOURCES_GAP_REM = "0.5rem"

/** Style du panneau sources : max-height ≈ N × hauteur ligne + (N−1) × gap. */
const getSourcesPanelMaxHeightStyle = computed(() => {
    const n = SOURCES_PANEL_MAX_VISIBLE_SOURCES
    return {
        maxHeight: `calc(${n} * ${SOURCES_PANEL_ESTIMATED_SOURCE_ROW_HEIGHT_REM} + ${n - 1} * ${SOURCES_PANEL_SOURCES_GAP_REM})`,
    }
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div
        class="dashboard-building-row relative flex flex-col gap-3"
        :style="{ zIndex: getRowZIndex }"
        data-cy="dashboard-building-row"
    >
        <!-- ================================ -->
        <!-- LIGNE BÂTIMENT (carte entière draggable) -->
        <!-- ================================ -->
        <div
            ref="cardRef"
            class="dashboard-building-row__card relative z-[2] flex w-full touch-none flex-col gap-2.5 rounded-xl border border-surface-200 bg-surface-0 p-5 shadow-sm transition-all hover:shadow-md"
            @click.capture="onBuildingCardClickCapture"
            @click="onBuildingCardNavigate"
            @contextmenu="onBuildingRowContextMenu"
            :class="[
                {
                    'dashboard-building-row__card--deep': getIsNested,
                    'opacity-0': isDragging,
                },
                props.draggable
                    ? 'touch-none cursor-pointer active:cursor-grabbing'
                    : 'cursor-pointer',
            ]"
            :style="getCardMinHeightStyle"
        >
            <div class="flex items-start justify-between gap-2">
                <div class="flex min-w-0 flex-1 items-start gap-2">
                    <BaseButton
                        v-if="getHasChildren"
                        v-bind="{ [BUILDING_CARD_NO_NAV_ATTR]: '' }"
                        :icon="
                            isExpanded
                                ? 'pi pi-chevron-down'
                                : 'pi pi-chevron-right'
                        "
                        text
                        rounded
                        severity="secondary"
                        aria-label="Afficher les enfants"
                        type="button"
                        @click="onToggleExpand"
                    />
                    <div class="flex min-w-0 flex-1 flex-col gap-2">
                        <ItemBuilding
                            :building="getItemBuildingData"
                            :show-tags="false"
                        />
                        <div
                            v-if="!props.disableTags"
                            v-bind="{ [BUILDING_CARD_NO_NAV_ATTR]: '' }"
                            class="dashboard-building-row__tags flex flex-col gap-2"
                            @click.stop
                        >
                            <div
                                class="flex flex-wrap items-center gap-2 text-sm text-surface-600"
                            >
                                <span
                                    >Étiquettes ({{
                                        getBuildingTagCount
                                    }})</span
                                >
                                <span aria-hidden="true">•</span>
                                <button
                                    type="button"
                                    class="m-0 cursor-pointer border-0 bg-transparent p-0 text-inherit underline hover:text-primary"
                                    @click="onOpenBuildingTagsDialog"
                                >
                                    Ajouter
                                </button>
                            </div>
                            <div
                                v-if="getBuildingTagLabels.length"
                                class="flex flex-wrap gap-1"
                            >
                                <BaseChip
                                    v-for="(
                                        label, idx
                                    ) in getBuildingTagsDisplayed"
                                    :key="`${label}-${idx}`"
                                    :label="label"
                                    removable
                                    :disabled="tagRemoving === label"
                                    @remove="onRemoveBuildingTag(label)"
                                />
                                <BaseChip
                                    v-if="getBuildingTagOverflowCount > 0"
                                    :label="`+${getBuildingTagOverflowCount}`"
                                    v-tooltip.top="{
                                        value: getBuildingTagsOverflowTooltipText,
                                    }"
                                    :aria-label="`Autres étiquettes : ${getBuildingTagsOverflowTooltipText}`"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="flex shrink-0 items-start gap-1"
                    v-bind="{ [BUILDING_CARD_NO_NAV_ATTR]: '' }"
                >
                    <div v-if="getHasSources" class="relative inline-flex">
                        <BaseButton
                            type="button"
                            text
                            rounded
                            severity="secondary"
                            draggable="false"
                            :icon="
                                isSourcesExpanded
                                    ? 'pi pi-chevron-up'
                                    : 'pi pi-chevron-down'
                            "
                            :aria-expanded="isSourcesExpanded"
                            :aria-label="
                                isSourcesExpanded
                                    ? 'Masquer les sources'
                                    : 'Afficher les sources'
                            "
                            @click="onToggleSources"
                        />
                        <span
                            class="pointer-events-none absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white"
                        >
                            {{ getSources.length }}
                        </span>
                    </div>
                    <BaseButton
                        icon="pi pi-ellipsis-v"
                        severity="secondary"
                        text
                        rounded
                        aria-label="Actions"
                        type="button"
                        @click="onToggleMenu"
                    />
                </div>
            </div>
            <BaseContextMenu ref="contextMenu" :model="contextMenuModel" />
            <div
                v-if="getHasSources && isSourcesExpanded"
                v-bind="{ [BUILDING_CARD_NO_NAV_ATTR]: '' }"
                class="dashboard-building-row__sources-panel flex flex-col gap-2 overflow-y-auto pt-3"
                :style="getSourcesPanelMaxHeightStyle"
            >
                <div
                    v-for="src in getSources"
                    :key="src._id_source ?? src.idsource ?? src.name"
                    class="cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                    role="button"
                    tabindex="0"
                    @contextmenu="onSourceRowContextMenu($event, src as Source)"
                    @click="onSourceCardActivate($event, src as Source)"
                    @keydown.enter="onSourceCardActivate($event, src as Source)"
                    @keydown.space.prevent="
                        onSourceCardActivate($event, src as Source)
                    "
                >
                    <ItemSource
                        :source="src as Source"
                        :building-id="getBuildingId"
                        :show-toggle-used="false"
                        @click:data="onSourceClickData"
                        @click:settings="onSourceClickSettings"
                    />
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- HIÉRARCHIE ENFANTS -->
        <!-- ================================ -->
        <div
            v-if="getHasChildren && isExpanded"
            class="dashboard-building-row__children relative z-[1] flex flex-col gap-3"
        >
            <div
                v-for="(child, index) in getChildren"
                :key="child._id_building ?? child._id"
                class="dashboard-building-row__child-wrap"
                :class="{
                    'dashboard-building-row__child-wrap--last':
                        index === getChildren.length - 1,
                }"
            >
                <HomeBuildingRow
                    :building="child"
                    :depth="(props.depth ?? 0) + 1"
                    :folder-list="props.folderList"
                    :folder-id="props.folderId"
                    :draggable="props.draggable"
                    :show-delete="props.showDelete"
                    :disable-tags="props.disableTags"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Arborescence parent → enfants (cf. legacy BuildingListItemGroup + BuildingListItem --deep) */
.dashboard-building-row {
    --tree-distance: 1.5rem;
    --tree-color: #dfe9ef;
}

.dashboard-building-row__children {
    margin-left: var(--tree-distance);
}

.dashboard-building-row__child-wrap {
    position: relative;
}

/* Ligne verticale + horizontale entre frères (sauf dernier) — sous la carte parente */
.dashboard-building-row__child-wrap:not(:last-child)::before {
    content: "";
    position: absolute;
    height: calc(100% + var(--tree-distance));
    left: calc(var(--tree-distance) * -1);
    border-left: 2px solid var(--tree-color);
    border-bottom: 2px solid var(--tree-color);
    border-color: var(--tree-color);
    pointer-events: none;
    z-index: 0;
}

/* Coude vers la carte : trait derrière le fond de la carte */
.dashboard-building-row__card--deep::before {
    content: "";
    position: absolute;
    height: calc(60% + var(--tree-distance));
    width: var(--tree-distance);
    left: calc(-1 * var(--tree-distance));
    top: calc(var(--tree-distance) * -1 - 0.25rem);
    border-left: 2px solid var(--tree-color);
    border-bottom: 2px solid var(--tree-color);
    border-radius: 0 0.25rem;
    border-color: var(--tree-color);
    pointer-events: none;
    z-index: -1;
}

/* Dernier enfant : arrondi du coude (legacy) */
.dashboard-building-row__child-wrap--last
    :deep(.dashboard-building-row__card--deep)::before {
    border-radius: 0 0 0 8px;
}

@media (max-width: 575.98px) {
    .dashboard-building-row {
        --tree-distance: 0.5rem;
    }

    .dashboard-building-row__card--deep::before {
        top: calc(var(--tree-distance) * -1 - var(--tree-distance) - 1rem);
    }
}
</style>
